import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Megaphone, Send, Eye, Users, MailX, RefreshCw } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const ESTADO = { en_curso: 'En curso', completado: 'Completado', interrumpido: 'Interrumpido' };

export const Stat = ({ icon: Icon, label, value, testid }) => (
  <div className="flex items-center gap-3 p-4 rounded-xl bg-violet-50">
    <Icon className="w-5 h-5 text-violet-600" />
    <div>
      <p className="text-2xl font-bold text-gray-900" data-testid={testid}>{value}</p>
      <p className="text-xs text-gray-500">{label}</p>
    </div>
  </div>
);

export const HistorialEnvios = ({ envios, testid = 'campana-historial' }) => (
  <div className="bg-white rounded-xl border" data-testid={testid}>
    <div className="p-4 border-b font-semibold text-gray-900">Historial de envíos</div>
    {envios.length === 0 ? (
      <p className="p-4 text-sm text-gray-500">Todavía no se envió la campaña.</p>
    ) : envios.map((e) => (
      <div key={e.id} className="p-4 border-b last:border-0 text-sm flex flex-wrap gap-x-6 gap-y-1" data-testid={`campana-envio-${e.id}`}>
        <span className="font-medium">{new Date(e.iniciado).toLocaleString('es-AR')}</span>
        <span data-testid={`campana-envio-estado-${e.id}`}>{ESTADO[e.estado] || e.estado}</span>
        <span>{e.enviados}/{e.total} enviados</span>
        {e.errores > 0 && <span className="text-red-600">{e.errores} con error</span>}
        <span className="text-gray-500">{e.iniciado_por}</span>
      </div>
    ))}
  </div>
);

export const CampanaPortalPanel = ({ onPreview }) => {
  const [data, setData] = useState(null);
  const [emailPrueba, setEmailPrueba] = useState('');
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const cargar = useCallback(async () => {
    const res = await axios.get(`${API}/api/campana-portal/resumen`, auth());
    setData(res.data);
  }, []);

  useEffect(() => { cargar(); }, [cargar]);
  const enCurso = data?.envios?.some((e) => e.estado === 'en_curso');
  useEffect(() => {
    if (!enCurso) return undefined;
    const t = setInterval(cargar, 5000);
    return () => clearInterval(t);
  }, [enCurso, cargar]);

  const run = async (fn, okText) => {
    setBusy(true); setMsg(null);
    try { await fn(); setMsg({ ok: true, text: okText }); await cargar(); }
    catch (err) { setMsg({ ok: false, text: err.response?.data?.detail || 'Ocurrió un error' }); }
    finally { setBusy(false); }
  };

  const enviarPrueba = () => run(
    () => axios.post(`${API}/api/campana-portal/prueba`, { email: emailPrueba }, auth()),
    `Prueba enviada a ${emailPrueba}`,
  );
  const enviarTodos = () => { setConfirmOpen(false); run(
    () => axios.post(`${API}/api/campana-portal/enviar`, { confirmar: true }, auth()),
    'Envío iniciado. Los mails salen de a uno; podés seguir el avance acá abajo.',
  ); };

  if (!data) return <div className="p-6 text-gray-500">Cargando campaña…</div>;

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 space-y-6" data-testid="campana-portal-panel">
      <div className="bg-white rounded-xl border p-6 space-y-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="font-semibold text-gray-900 flex items-center gap-2">
              <Megaphone className="w-5 h-5 text-violet-600" /> Campaña “Sumate al Portal”
            </h2>
            <p className="text-sm text-gray-500 mt-1">Mail a todos los matriculados con email que todavía no crearon su cuenta. El texto se edita en la pestaña Templates (“Campaña · Sumate al Portal”).</p>
          </div>
          <div className="flex gap-2">
            <button onClick={onPreview} data-testid="campana-preview-btn" className="flex items-center gap-2 px-3 py-2 text-sm border rounded-lg hover:bg-gray-50"><Eye className="w-4 h-4" /> Ver mail</button>
            <button onClick={cargar} data-testid="campana-refresh-btn" className="flex items-center gap-2 px-3 py-2 text-sm border rounded-lg hover:bg-gray-50"><RefreshCw className="w-4 h-4" /> Actualizar</button>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Stat icon={Users} label="Lo van a recibir" value={data.destinatarios} testid="campana-destinatarios" />
          <Stat icon={MailX} label="Excluidos por haberse dado de baja de los mails" value={data.omitidos_baja} testid="campana-omitidos-baja" />
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <input type="email" value={emailPrueba} onChange={(e) => setEmailPrueba(e.target.value)} placeholder="tu@email.com" data-testid="campana-email-prueba-input" className="flex-1 px-3 py-2 border rounded-lg text-sm" />
          <button disabled={busy || !emailPrueba} onClick={enviarPrueba} data-testid="campana-enviar-prueba-btn" className="px-4 py-2 text-sm border border-violet-600 text-violet-700 rounded-lg hover:bg-violet-50 disabled:opacity-50">Enviarme una prueba</button>
          <button disabled={busy || enCurso || data.destinatarios === 0} onClick={() => setConfirmOpen(true)} data-testid="campana-enviar-todos-btn" className="flex items-center justify-center gap-2 px-4 py-2 text-sm bg-violet-600 hover:bg-violet-700 text-white rounded-lg disabled:opacity-50"><Send className="w-4 h-4" /> Enviar a {data.destinatarios}</button>
        </div>
        {msg && <p data-testid="campana-mensaje" className={`text-sm ${msg.ok ? 'text-green-700' : 'text-red-600'}`}>{msg.text}</p>}
      </div>
      <HistorialEnvios envios={data.envios} />
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="campana-confirm-dialog">
          <DialogHeader><DialogTitle>¿Enviar la campaña?</DialogTitle></DialogHeader>
          <p className="text-sm text-gray-600">Se va a mandar el mail a <b>{data.destinatarios}</b> matriculados sin cuenta. No se puede deshacer.</p>
          <DialogFooter>
            <button onClick={() => setConfirmOpen(false)} data-testid="campana-confirm-cancel" className="px-4 py-2 text-sm border rounded-lg">Cancelar</button>
            <button onClick={enviarTodos} data-testid="campana-confirm-ok" className="px-4 py-2 text-sm bg-violet-600 text-white rounded-lg">Sí, enviar</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
